/*
 * Copyright (c) 2018 CalAmp Corp.  All Rights Reserved
 */
import Excel from 'exceljs';
import {saveAs} from 'file-saver';
import {translate} from '../locale/locale';
import {
  CTCEntity,
  Entity,
  EntityStatus,
  GenericObject
} from '../types/entities';
import {
  ExtendedErrorType
} from '../types/services';
import {
  DownloadTemplate,
  TemplateColumn,
  TemplateColumns,
  TemplateColumnTypes,
  TemplateValidation
} from '../types/templates';
import {safeJSONParse} from '../utils/JSONUtils';
import {formatRequirements} from '../utils/StringUtil';
import {template} from './Templates';

interface GenerateDownloadType {
  workbook: Excel.Workbook;
  filename: string;
}

interface ApplyColumnDefinitions extends DownloadTemplateDeviceTypes {
  isDevice: boolean;
  worksheet: Excel.Worksheet;
  columns: TemplateColumn[],
  validations: TemplateValidation[],
  rowLength?: number;
  rows?: CTCEntity[];
}

interface ApplyCellValidation {
  worksheet: Excel.Worksheet;
  validation: TemplateValidation;
  row: number;
  column: string;
}

interface CreateValidationType {
  required: boolean;
  conditionallyRequired?: boolean;
  error: string;
  type: TemplateColumnTypes;
  index: number;
}

interface CreateLookupSheetType {
  sheetName: string;
  optionString: string;
  workbook: Excel.Workbook;
}

interface DownloadTemplateTypes extends DownloadTemplate, DownloadTemplateDeviceTypes {
  ignoreEntity?: boolean;
}

export interface DownloadTemplateDeviceTypes {
  isDeviceImport?: boolean;
  isValidFile?: boolean;
}

type CreateSubSheetType = {
  sheetName: string;
  columns: GenericObject[];
  rows: GenericObject[][];
  targetCell?: string;
  workbook: Excel.Workbook;
};

const WORKBOOK_CREATOR = 'CalAmp';

/**
 * @private
 *
 * Defines the template header column cell styles for required columns and
 * optional columns.
 */
const styles = {
  alignment : {vertical: 'middle', horizontal: 'center'},
  required  : {
    font : {size: 16, color: {argb: '00000000'}},
    fill : {
      type    : 'pattern',
      pattern : 'solid',
      fgColor : {argb: 'FFFF0000'}
    }
  },
  conditionallyRequired : {
    font : {size: 16, color: {argb: '00000000'}},
    fill : {
      type    : 'pattern',
      pattern : 'solid',
      fgColor : {argb: 'EE8C31'}
    }
  },
  optional : {
    font : {size: 16, color: {argb: '00000000'}},
    fill : {
      type    : 'pattern',
      pattern : 'solid',
      fgColor : {argb: '92D050'}
    }
  }
};

// defines the list of possible xlsx column positions ...
const columnPositions = 'A,B,C,D,E,F,G,H,I,J,K,L,M,N,O,P,Q,R,S,T,U,V,W,X,Y,Z,AA,AB,AC'.split(',');

/**
 * Provides a utility function which manages the dynamic generation of an XLSX
 * spreadsheet for download by the User.
 *
 * @param entity title to be displayed in the downloaded template.
 * @param title The title to be displayed in the downloaded template.
 * @param filename The name of the downloaded template.
 * @param columns An array of localized asset template column definitions.
 * @param options An array of option columns.
 * @returns the XLSX spreadsheet
 */
export const downloadBlankTemplate = ({
  entity,
  title,
  filename,
  columns,
  options = {},
  rows,
  ignoreEntity = false,
  isValidFile = false,
  isDeviceImport = false
}: DownloadTemplateTypes) => {
  const isDevice   = ignoreEntity ? false : entity === Entity.device;
  const workbook   = new Excel.Workbook();
  workbook.created = new Date();
  workbook.creator = WORKBOOK_CREATOR;
  let worksheet = null;
  if (isDevice) {
    worksheet  = workbook.addWorksheet(title, {views: [{state: 'frozen', xSplit: rows?.length ? 5 : 4, ySplit: 9}]});
  } else {
    worksheet  = workbook.addWorksheet(title);
  }
  const {columnEntries, columnValidations} = template({entity, columns, workbook, options});
  applyColumnDefinitions({
    isDevice,
    worksheet,
    columns     : columnEntries,
    validations : columnValidations,
    rows,
    isDeviceImport
  });
  if (isValidFile) {
    generateCSVDownload({
      workbook,
      filename : filename ?? title
    });
  } else {
    return generateDownload({
      workbook,
      filename : filename ?? title
    });
  }
};

/**
 * Returns the localized formulae rules for the given template column.
 *
 * @param key The localization key; for example 'common:entityStatus',
 * 'common:booleans', etc.
 * @returns the localized formulae
 */
export const localizeFormulae = (key) => {
  const entries  = Object.values(translate(key, {returnObjects: true}));
  return entries;
};

/**
 * Given a validation object, replaces the __items__ token in the localized
 * error property with the given list of items.
 *
 * @param validation An object containing column validation.
 *        This method will replace the error property of the validation
 *        object to provide formatting of the localized error, replacing
 *        it's __items__ token with the given items specified via the
 *        items argument.
 * @param items An items of strings which defined the validation
 *        list items which will replace the __items__ token.
 * @rtruns the localized error.
 */
export const interpolateValidation = (validation, items) => {
  return formatRequirements(
    validation.error.replace('__items__', items), translate('common:conjunctions.or')
  );
};

/**
 * Given a worksheet instance, a cell/column validation, and a cell (as specified
 * by its row and column), applies the validation to the cell.
 *
 * @param worksheet The Excel.Workbook.worksheet
 *        instance to which the cell/column validation is to be applied.
 * @param validation The cell/column validation to be applied.
 * @param row The row of the current cell.
 * @param column The column of the current cell.
 *
 */
const applyCellValidation = ({worksheet, validation, row, column}: ApplyCellValidation) => {
  const dataValidation: Excel.DataValidation = (Object.assign({}, validation,
    (validation.type === 'custom')
      ? {formulae: [(validation.formulae[0] as string).replace(/__col__/g, `$${column}`).replace(/__row__/g, `$${row}`)]}
      : {}) as Excel.DataValidation);
  worksheet.getCell(`${column}${row}`).dataValidation = dataValidation;
};

/**
 * Given a worksheet instance and a list of columns and corresponding validations,
 * applies header formatting and column validation.
 *
 * @param worksheet The Excel.Workbook.worksheet
 *        instance to which the column definitions are to be applied.
 * @param columns An array of spreadsheet columns which are to have
 *        validation rules, and column header formatting applied.
 * @param validations An array of spreadsheet columns validations
 *        which are to be applied to the given columns.
 * @param rows The number of rows to which the validations are to
 *        be applied (defaults to 1000).
 */
const applyColumnDefinitions = ({
  isDevice,
  worksheet,
  columns,
  validations,
  rows,
  rowLength = 999,
  isDeviceImport = false
}: ApplyColumnDefinitions) => {
  const {alignment, required, conditionallyRequired, optional} = styles;
  worksheet.columns = columns;
  if (isDevice) {
    const instructions = Object.keys(translate('admin-devices:import.instructions', {returnObjects: true}));
    const row          = worksheet.getRow(instructions.length + 1);
    row.values         = columns?.map(({header}) => header);
    const addBulk      = translate(`admin-devices:import.instructions.${8}`);
    const importBulk   = translate('admin-devices:import.emptyIdentifiersMessage.importMessage');

    let i = 1;
    while (i <= instructions.length) {
      worksheet.getRow(i).values = [translate(`admin-devices:import.instructions.${i}`)];
      isDeviceImport ? worksheet.getRow(8).values = [addBulk?.replace(addBulk, importBulk)] : null;
      worksheet.getRow(i).border = {
        top    : {style: 'thin', color: {argb: 'FFFFFFFF'}},
        left   : {style: 'thin', color: {argb: 'FFFFFFFF'}},
        bottom : {style: 'thin', color: {argb: 'FFFFFFFF'}},
        right  : {style: 'thin', color: {argb: 'FFFFFFFF'}}
      };
      i++;
    }
  }
  if (rows?.length) {
    worksheet.addRows(rows);
  }
  if (validations?.length) {
    validations.forEach(validation => {
      const column = columnPositions[validation.index];
      const header = worksheet.getCell(isDevice ? `${column}10` : `${column}1`);
      header.alignment = alignment as Excel.Alignment;
      if (!validation.allowBlank) {
        header.fill = required.fill as Excel.Fill;
        header.font = required.font;
      } else if (validation.conditionallyRequired) {
        header.fill = conditionallyRequired.fill as Excel.Fill;
        header.font = conditionallyRequired.font;
      } else {
        header.fill = optional.fill as Excel.Fill;
        header.font = optional.font;
      }
      let i = 1;
      while (i <= rowLength) {
        applyCellValidation({worksheet, validation, row: i, column});
        i++;
      }
    });
  }
};

/**
 * Given an exceljs and filename, dynamically generates a spreadsheet for
 * download to the client.
 *
 * @param workbook The Excel.Workbook instance which is to
 *        be used for generating the spreadsheet for download to the user's
 *        machine.
 * @param filename The name of the spreadsheet to be downloaded.
 */
const generateDownload = ({workbook, filename}: GenerateDownloadType) => {
  const exporter = async() => {
    const data = await workbook.xlsx.writeBuffer();
    saveAs(new Blob(
      [data],
      {type: 'application/octet-stream'}
    ), `${filename}.xlsx`);
    return true;
  };
  return exporter();
};

/**
 * Given an exceljs and filename, dynamically generates a spreadsheet for
 * download to the client in .csv format.
 *
 * @param workbook The Excel.Workbook instance which is to
 *        be used for generating the spreadsheet for download to the user's
 *        machine.
 * @param filename The name of the spreadsheet to be downloaded.
 */
const generateCSVDownload = ({workbook, filename}: GenerateDownloadType) => {
  const exporter = async() => {
    const worksheet = workbook.getWorksheet(1);
    const data      = [];

    worksheet.eachRow((row) => {
      const rowValues = [];
      row.eachCell((cell) => {
        rowValues.push(cell.value);
      });
      data.push(rowValues);
    });

    const csvString = data.map(row => row.join(',')).join('\n');
    const blob      = new Blob(
      [csvString],
      {type: 'text/csv;charset=utf-8'}
    );

    saveAs(blob, `${filename}.csv`);

    return true;
  };
  return exporter();
};

/**
 * Provides a utility function which manages the generation of validation object for
 * downloadable templates.
 *
 * @param required checks if the column is mandatory
 * @param error the error message if validation fails
 * @param index the column position
 * @param type data type of cell values
 */
export const createValidation = ({
  required, error, index, type, conditionallyRequired = false
}: CreateValidationType): TemplateValidation => {
  return {
    allowBlank       : !required,
    showErrorMessage : true,
    conditionallyRequired,
    error,
    index,
    type
  };
};

/**
 * Provides a utility function which manages the generation of status column with
 * validation for all entities.
 *
 * @param validation the validation object for status column.
 * @returns the status column with validation.
 */
export const statusColumn = ({validation}: TemplateColumns): TemplateValidation => {
  const status         = localizeFormulae('common:entityStatus');
  validation.error     = interpolateValidation(validation, status);
  validation.formulae  = [`"${status}"`];
  const filteredStatus = status.filter((value) => {
    return value !== EntityStatus.Deleted;
  });
  validation.error    = interpolateValidation(validation, filteredStatus);
  validation.formulae = [`"${filteredStatus}"`];
  return validation;
};

/**
 * Utility which helps in formatting the import errors/failures to be readable before user downloads the
 * same via excel ...
 *
 * ```typescript
 *
 * import {formatErrors} from '@ion/core/src/xlsx';
 *
 * const formattedErrors = formatErrors(errors, Entity.user);
 * ```
 *
 * @param   errors the errors/failures which are to be formatted
 * @param   entity the current entity
 * @returns the formatted errors/failures
 */
export const formatErrors = (
  errors: CTCEntity[] | ExtendedErrorType[],
  entityLabel
): ExtendedErrorType[] => {
  const formattedErrors: ExtendedErrorType[] = [];
  errors?.forEach(error => {
    const {response} = error;
    const entity      = error.data || response?.config?.data;
    const errorMsg    = response?.data?.response?.errors?.[0] || error.message;
    const entityValue = safeJSONParse(entity);
    const formattedError: ExtendedErrorType = {
      name          : error.name,
      code          : error.errorCode,
      message       : errorMsg,
      [entityLabel] : (entity && entityValue) ? entityValue[entityLabel] : error[entityLabel],
      error
    };
    formattedErrors.push(formattedError);
  });
  return formattedErrors;
};

/**
 * Utility which helps in formatting the import errors/failures to be readable before user downloads the
 * same via excel ...
 *
 * ```typescript
 *
 * import {formatBulkErrors} from '@ion/core/src/xlsx';
 *
 * const formattedErrors = formatBulkErrors(errors, Entity.user);
 * ```
 *
 */
export const formatBulkErrors = (
  errors: CTCEntity[] | ExtendedErrorType[],
  entityLabel
): ExtendedErrorType[] => {
  const formattedErrors: ExtendedErrorType[] = [];
  errors?.forEach(error => {
    const formattedError: ExtendedErrorType = {
      name          : error.username,
      code          : error.code,
      message       : error.description,
      [entityLabel] : error || null,
      error
    };
    formattedErrors.push(formattedError);
  });
  return formattedErrors;
};

/**
 * Utility which helps in creating a separate look up sheet of dropdown lists
 * for data validations and returns the formulae.
 *
 * ```typescript
 *
 * import {createLookupSheet} from '@ion/core/src/xlsx';
 *
 * const formattedErrors = createLookupSheet({
 *   headerName: header,
 *   sheetName: 'AssetType',
 *   options: options?.assetTypes,
 *   workbook
 * });
 *
 * ```
 *
 * @param   sheetName the lookup sheet name
 * @param   options the dropdown options list
 * @param   workbook the workbook with which the lookup sheet is attached
 * @returns the lookupsheet data validation formulae
 */
export const createLookupSheet = ({
  sheetName,
  optionString,
  workbook
}: CreateLookupSheetType) => {
  const options = optionString?.split(',');
  const dataValidationSheet = workbook.addWorksheet(sheetName);
  dataValidationSheet.protect(sheetName, {});
  for (const option of options) {
    dataValidationSheet.addRow([option]);
  }
  return [`=${sheetName}!$A$1:$A$${options?.length}`];
};

/**
 * Creates a new worksheet in the provided workbook, sets columns and rows, applies
 * styles to column headers, and optionally returns a formula reference.
 *
 * ```typescript
 *
 * import {createLookupSheet} from '@ion/core/src/xlsx';
 *
 * createSubSheet({
 *   sheetName: 'Drivers',
 *   columns,
 *   rows,
 *   workbook
 * });
 *
 * ```
 *
 * @param sheetName - The name of the sheet to be created.
 * @param columns - The columns configuration for the sheet.
 * @param rows - The rows to be added to the sheet.
 * @param workbook - The workbook to which the sheet will be added.
 * @param targetCell - The target cell reference for a formula (optional).
 * @returns Returns a formula reference if targetCell is provided.
 */
export const createSubSheet = ({
  sheetName,
  columns,
  rows,
  workbook,
  targetCell
}: CreateSubSheetType) => {
  const sheetRef = workbook.addWorksheet(sheetName);
  sheetRef.protect(sheetName, {});
  sheetRef.columns = columns.map(column => ({
    ...column, header : column.header.replace('__group__', '')
  }));
  // Apply styles to column headers
  const headerRow = sheetRef.getRow(1);
  headerRow.eachCell((cell) => {
    cell.fill = styles.optional.fill as Excel.Fill;
    cell.font = styles.optional.font;
    cell.alignment = styles.alignment as Excel.Alignment;
  });
  rows.forEach(row => sheetRef.addRow(row));
  if (targetCell) {
    return [`=${sheetName}!$${targetCell}$2:$${targetCell}$${rows?.length + 1}`];
  }
};

/**
 * The formatTabData returns header name for each specific tabs.
 *
 * ```typescript
 *
 * import {formatTabData} from '@ion/core/src/xlsx';
 *
 * formatTabData(headers, data);
 * 
 * ```
 *
 */
export const formatTabData = (headers, data) => {
  const [
    headerNames,
    fieldNames
  ] = headers?.reduce(
    ([names, fields], header) => [
      [...names, header?.headerName],
      [...fields, header?.field]
    ],
    [[], []]
  );

  const rows = [headerNames];

  data?.forEach(item => {
    const row = fieldNames?.map(field => item[field] || '');
    rows.push(row);
  });

  return rows;
};
